import { getAdminToken } from "../app/admin/admin_auth";
/**
 * integrations.ts — external-integration & data-source SEAM.
 *
 * Purpose: keep the (frozen) data-connection layer untouched while making the
 * redesigned UI pluggable. The UI depends ONLY on these interfaces; swapping a
 * provider (mock -> real DB API, none -> kakao nav) is a one-file change here.
 *
 * Nothing in this file talks to the live MQTT/WebRTC transport — that stays in
 * src/interface/**. Empty bindings are explicit `TODO(data)` so they can be
 * wired (or removed) later without touching call sites.
 */

// ---------------------------------------------------------------------------
// Feature flags — turn capabilities on/off without code changes downstream.
// ---------------------------------------------------------------------------
export interface FeatureFlags {
  yolo: boolean;
  metricsGraph: boolean;
  twoWayVoice: boolean;
  externalDisplay: boolean;
  immersiveEffects: boolean; // accel/decel/turn sweeps, corridor, etc.
}

export const featureFlags: FeatureFlags = {
  yolo: false,
  metricsGraph: false,
  twoWayVoice: true,
  externalDisplay: true,
  immersiveEffects: true,
};

// ---------------------------------------------------------------------------
// Navigation provider — global route. Kakao is opt-in via an admin-set key.
// Short-term steering-based path overlay is always available (client-side),
// independent of this provider.
// ---------------------------------------------------------------------------
export type NavProviderKind = "none" | "kakao";

export interface NavSettings {
  provider: NavProviderKind;
  /** Kakao REST/JS key. Empty until set in Admin > 환경설정. TODO(data): load from admin/DB. */
  kakaoApiKey: string;
}

export const navSettings: NavSettings = {
  provider: "kakao",
  // Provided for dev. TODO(data): move to Admin settings / DB; restrict by domain.
  kakaoApiKey: "08112c509a9a248c6d8958652a952964",
};

export function isGlobalNavEnabled(s: NavSettings = navSettings): boolean {
  return s.provider === "kakao" && s.kakaoApiKey.trim().length > 0;
}

// ---------------------------------------------------------------------------
// Account / vehicle-metadata data source — designed to be split into a DB API
// later. Today: a read-only mock. Replace `accountSource` impl only.
// ---------------------------------------------------------------------------
export type ControlPermission = "direct" | "indirect" | "monitor";

export interface VehicleMeta {
  vehicleId: string; // server-provided (read-only)
  nickname: string; // client-configurable
  photoUrl?: string; // client-configurable
  color?: string; // client-configurable
  note?: string; // client-configurable
}

export interface AccountMapping {
  userId: string;
  role: "admin" | "operator" | "viewer";
  vehicles: string[]; // allowed vehicleIds, or ["*"] for fleet
  permissions: ControlPermission[];
  active: boolean;
}

export interface AccountSource {
  listAccounts(): Promise<AccountMapping[]>;
  listVehicleMeta(): Promise<VehicleMeta[]>;
  saveVehicleMeta(meta: VehicleMeta): Promise<void>;
  saveAccount(account: AccountMapping): Promise<void>;
}

/**
 * 차량 별칭 기본값 — 서버에 저장된 값이 없을 때만 쓰인다.
 *
 * 서버에 한 번이라도 저장하면 그 값이 우선한다(같은 vehicleId 를 덮어쓴다).
 */
const DEFAULT_VEHICLE_META: VehicleMeta[] = [
  { vehicleId: "veh_IONIQ5", nickname: "IONIQ5 — A동", color: "#3a8fd0" },
  { vehicleId: "veh_GV60", nickname: "GV60 — 셔틀1" },
  { vehicleId: "veh_GV80", nickname: "GV80 — 테스트" },
  { vehicleId: "veh_CARNIVAL", nickname: "CARNIVAL — 물류" },
];

const VEHICLE_META_ENDPOINT = "/api/vehicle-meta";

/** 저장된 값이 기본값을 덮어쓴다. 저장된 적 없는 차량은 기본값이 남는다. */
export function mergeVehicleMeta(
  defaults: VehicleMeta[],
  stored: VehicleMeta[],
): VehicleMeta[] {
  const byId = new Map(defaults.map((m) => [m.vehicleId, m]));
  stored.forEach((m) => {
    if (m && typeof m.vehicleId === "string" && m.vehicleId) {
      byId.set(m.vehicleId, { ...byId.get(m.vehicleId), ...m });
    }
  });
  return [...byId.values()];
}

/**
 * Mock source — wireframe-confirmed sample data, read-only persistence stubbed.
 * TODO(data): replace with a real DB-backed API client when the account/auth
 * service is split out. Keep this interface stable so call sites don't change.
 */
export const mockAccountSource: AccountSource = {
  async listAccounts() {
    return [
      { userId: "usr_test1", role: "operator", vehicles: ["veh_IONIQ5", "veh_GV60"], permissions: ["direct", "indirect"], active: true },
      { userId: "usr_admin", role: "admin", vehicles: ["*"], permissions: ["direct", "indirect", "monitor"], active: true },
      { userId: "usr_view2", role: "viewer", vehicles: ["veh_GV80"], permissions: ["monitor"], active: false },
    ];
  },
  async listVehicleMeta() {
    return DEFAULT_VEHICLE_META;
  },
  async saveVehicleMeta() {
    /* TODO(data): persist to DB API */
  },
  async saveAccount() {
    /* TODO(data): persist to DB API */
  },
};

/**
 * 차량 별칭은 서버 파일에 저장한다.
 *
 * 예전엔 saveVehicleMeta 가 빈 함수라, 화면에서 이름을 바꾸면 "저장됨" 토스트만 뜨고
 * 새로고침하면 되돌아갔다. 계정/권한은 아직 목 데이터를 쓴다(별도 인증 서비스 대상).
 *
 * 쓰기에는 Admin 토큰을 실어 보낸다 — 클라이언트 로그인 게이트만으로는 API 를 직접
 * 호출하는 걸 막지 못하므로, 서버가 토큰 없는 쓰기를 거절한다.
 */
export const httpVehicleMetaSource: AccountSource = {
  listAccounts: mockAccountSource.listAccounts,
  saveAccount: mockAccountSource.saveAccount,

  async listVehicleMeta() {
    try {
      const response = await fetch(VEHICLE_META_ENDPOINT);
      if (!response.ok) {
        return DEFAULT_VEHICLE_META;
      }
      const stored = await response.json();
      return mergeVehicleMeta(
        DEFAULT_VEHICLE_META,
        Array.isArray(stored) ? stored : [],
      );
    } catch {
      // 서버가 없으면(로컬 dev 서버 등) 기본값으로 계속 동작한다.
      return DEFAULT_VEHICLE_META;
    }
  },

  async saveVehicleMeta(meta) {
    const token = getAdminToken();
    if (!token) {
      throw new Error("Admin 로그인이 필요합니다");
    }
    const response = await fetch(VEHICLE_META_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Admin-Token": token },
      body: JSON.stringify(meta),
    });
    if (!response.ok) {
      throw new Error(
        response.status === 403
          ? "저장 권한이 없습니다 (Admin 로그인 확인)"
          : `저장 실패 (${response.status})`,
      );
    }
  },
};

/** Active source. Swap to a DB-backed impl when the service is separated. */
export const accountSource: AccountSource = httpVehicleMetaSource;
